/**
 * Perfil de cliente devuelto por `/identify-user` bajo la key `client`.
 * Mapea exactamente los campos `$fillable` de `App\Models\Client.php`.
 */
export interface Client {
  id: number;
  name: string | null;
  surnames: string | null;
  identification: string | null;
  email: string;
  phone: string | null;
  /**
   * Dirección legacy en texto libre. Mantener como fallback de lectura
   * para clientes antiguos cuyos datos aún no pasaron por el checkout
   * nuevo. NUNCA escribir aquí desde el checkout nuevo (queda en null).
   */
  address: string | null;
  province: string | null;
  canton: string | null;
  district: string | null;
  hood: string | null;
  directions: string | null;
  user_id: number | null;
  newsletter: boolean;
  is_vip: boolean;
  is_known: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface User {
  id: number;
  name: string;
  /**
   * Apellidos de la cuenta. `/identify-user` los serializa (columna `users.surnames`);
   * opcional porque otros flujos que construyen `User` no los traen.
   */
  surnames?: string | null;
  /**
   * Teléfono de la cuenta (columna `users.phone`, no está en `$hidden`): `/identify-user` lo serializa y
   * `PUT /profile` lo escribe (A3 de B10). Opcional porque otros flujos que construyen `User` no lo traen; no es
   * `client.phone`, que es la ficha del checkout.
   */
  phone?: string | null;
  email: string;
  /**
   * Solo el NOMBRE del fichero (`App\Models\User`, subido por `POST /user-avatar`), no una URL: para pintarlo,
   * `userAvatarUrl()` (`shared/utils/user-avatar.util.ts`).
   */
  avatar?: string;
  email_verified_at?: string;
  /**
   * Tipo de cuenta: `admin` | `regular` | `cliente` | `juez` (`App\Models\User::USUARIO_*`). `/identify-user`
   * lo serializa (`type` está en `$fillable` y no en `$hidden`). Opcional porque otros flujos que construyen
   * `User` no lo traen; para preguntar «¿es cliente?», `isClientAccount()`.
   */
  type?: string;
  client_id?: number;
  /**
   * Perfil del cliente embebido. Lo devuelve el endpoint /identify-user.
   * Disponible cuando el usuario es de tipo CLIENTE; null/undefined para
   * otros tipos (admin, etc.).
   */
  client?: Client | null;
  /**
   * Saldo de Perrón Points y nivel Early Access. Los devuelve /identify-user
   * (D17); opcionales porque otros flujos que construyen `User` sin pasar
   * por ese endpoint (p. ej. `register()`) no los traen.
   */
  perron_points?: number;
  early_access?: boolean;
  /**
   * Correo verificado (B24 de B10). La API lo serializa con cast `boolean` (`App\Models\User::$casts`);
   * solo las cuentas `cliente` reciben el correo de verificación. Opcional porque otros flujos que
   * construyen `User` no lo traen: `undefined` no es «sin verificar».
   */
  verified?: boolean;
  /**
   * Alta de la cuenta, ISO 8601 de `/identify-user` (`2026-09-30T17:10:00.000000Z`, formato por defecto de
   * Laravel 9). Para «Cliente desde {mes} de {año}» en la cabecera del perfil (W0b).
   */
  created_at?: string;
}

/**
 * ¿Es una cuenta de cliente de la tienda? Solo esas reciben el correo de verificación (B24), así que solo a
 * ellas tiene sentido avisarles de que no lo han verificado. Sin `type` (flujos que no pasan por
 * `/identify-user`) no se asume nada: `false`.
 */
export function isClientAccount(user: User | null): boolean {
  return user?.type === 'cliente';
}

export interface LoginRequest {
  grant_type: 'password';
  client_id: string;
  client_secret: string;
  username: string;
  password: string;
}

export interface LoginResponse {
  token_type: string;
  expires_in: number;
  access_token: string;
  refresh_token: string;
}

export interface RegisterRequest {
  name: string;
  surnames: string;
  email: string;
  password: string;
  password_confirmation: string;
  newsletter?: boolean;
  type?: 'cliente';
}
